import React from 'react';
import { getGroupedFields, type SearchFilterFieldDef } from '@/utils/searchCategoryConfig';
import type { SearchAdminFormState } from '@/utils/searchAdminForm';

type Props = {
  category: string;
  form: SearchAdminFormState;
  onChange: (next: SearchAdminFormState) => void;
  variant?: 'admin' | 'wizard';
};

function wizardFieldClass(field: SearchFilterFieldDef): string | undefined {
  if (field.type === 'textarea' || field.gridClass?.includes('search-text')) {
    return 'wizard-field-span-2';
  }
  return undefined;
}

export default function SearchFilterFields({
  category,
  form,
  onChange,
  variant = 'admin',
}: Props) {
  const groups = getGroupedFields(category);
  const isWizard = variant === 'wizard';

  function setField<K extends keyof SearchAdminFormState>(key: K, value: SearchAdminFormState[K]) {
    onChange({ ...form, [key]: value });
  }

  const fieldsContent = groups.map(({ group, label, fields }) => {
    const fieldNodes = fields.map((field) => {
      const className = isWizard ? wizardFieldClass(field) : (field.gridClass ?? undefined);
      const value = form[field.key];

      if (field.type === 'textarea') {
        return (
          <label key={field.key} className={className}>
            {field.label}
            {field.hint && <span className="hint"> {field.hint}</span>}
            <textarea
              rows={3}
              value={String(value ?? '')}
              placeholder={field.placeholder}
              onChange={(e) => setField(field.key, e.target.value)}
            />
          </label>
        );
      }

      if (field.type === 'select') {
        return (
          <label key={field.key} className={className}>
            {field.label}
            <select
              value={String(value ?? '')}
              onChange={(e) =>
                setField(field.key, e.target.value as SearchAdminFormState[typeof field.key])
              }
            >
              {(field.options ?? []).map((opt) => (
                <option key={opt.value || '__any__'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        );
      }

      return (
        <label key={field.key} className={className}>
          {field.label}
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            value={String(value ?? '')}
            placeholder={field.placeholder}
            onChange={(e) => setField(field.key, e.target.value)}
          />
        </label>
      );
    });

    if (isWizard) {
      return <React.Fragment key={group}>{fieldNodes}</React.Fragment>;
    }

    return (
      <fieldset key={group} className="search-form__group">
        <legend className="search-form__group-legend">{label}</legend>
        {fieldNodes}
      </fieldset>
    );
  });

  if (isWizard) {
    return <div className="wizard-field-grid">{fieldsContent}</div>;
  }

  return <>{fieldsContent}</>;
}
